import React from 'react';
import { motion } from 'framer-motion';
import { Heart, ArrowDown } from 'lucide-react';
import { Button } from './ui/button';

const GIVEBUTTER_URL = "https://givebutter.com/XT2qPn";

const HeroSection = ({ onDonateClick }) => {
  const scrollToMission = () => {
    const element = document.querySelector('#mission');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDonate = () => {
    window.open(GIVEBUTTER_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <section 
      className="min-h-screen relative flex items-center justify-center overflow-hidden bg-white"
      data-testid="hero-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231E3A8A' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Overline */}
          <motion.p 
            className="text-xs font-bold tracking-[0.2em] uppercase text-[#1E3A8A] mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            501(c)(3) Nonprofit Organization
          </motion.p>

          {/* Main Heading */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-medium text-[#0B1120] tracking-tight leading-[1.1] mb-8">
            Empowering Lives
            <br />
            <span className="text-[#1E3A8A]">Through Service</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl text-slate-600 font-light max-w-2xl mx-auto mb-12 leading-relaxed">
            The Prestigious Travel Club Foundation transforms communities through 
            feeding programs, veteran support, children's healthcare, and mentorship—
            because real change starts with genuine compassion.
          </p>

          {/* CTA Button */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              variant="outline"
              onClick={scrollToMission}
              className="border-2 border-[#1E3A8A] hover:bg-[#1E3A8A] text-[#1E3A8A] hover:text-white px-10 py-6 text-lg rounded-full transition-all"
              data-testid="hero-learn-more-button"
            >
              Learn Our Story
            </Button>
          </div>
        </motion.div>

        {/* Stats Preview */}
        <motion.div
          className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-8 border-t border-slate-200 pt-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          {[
            { number: '12,500+', label: 'Families Fed' },
            { number: '3,200+', label: 'Children Supported' },
            { number: '850+', label: 'Veterans Assisted' },
            { number: '15K+', label: 'Mentorship Hours' },
          ].map((stat, idx) => (
            <div key={idx} className="text-center" data-testid={`hero-stat-${idx}`}>
              <p className="text-3xl sm:text-4xl font-bold text-[#1E3A8A] font-serif">{stat.number}</p>
              <p className="text-sm text-slate-500 uppercase tracking-wider mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <button 
            onClick={scrollToMission}
            className="text-slate-400 hover:text-[#1E3A8A] transition-colors"
            aria-label="Scroll down"
          >
            <ArrowDown className="w-6 h-6" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
